import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  FileText,
  CheckCircle,
  Clock,
  RefreshCw,
  Eye,
  ChevronRight,
  ArrowRightLeft,
  AlertCircle,
  Brain,
  Play
} from 'lucide-react';

export default function UserAssessments() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showRetakeDialog, setShowRetakeDialog] = useState(false);
  const [retakeType, setRetakeType] = useState(null);
  const [starting, setStarting] = useState(false);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [compareType, setCompareType] = useState(null);
  const [compareAssessments, setCompareAssessments] = useState([]);
  const [selectedForCompare, setSelectedForCompare] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/dashboard/assessments');
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleRetake = (type) => {
    setRetakeType(type);
    setShowRetakeDialog(true);
  };

  const confirmRetake = async () => {
    try {
      setStarting(true);
      const response = await axios.post(`/api/dashboard/assessments/${retakeType}/retake`);
      toast.success('New assessment started!');
      window.location.href = `/assessment/${retakeType}/${response.data.assessmentId}`;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start assessment');
    } finally {
      setStarting(false);
      setShowRetakeDialog(false);
    }
  };

  const openCompareDialog = (type, assessments) => {
    const completed = assessments.filter(a => a.status === 'completed');
    if (completed.length < 2) {
      toast.error('You need at least 2 completed assessments to compare');
      return;
    }
    setCompareType(type);
    setCompareAssessments(completed);
    setSelectedForCompare([]);
    setShowCompareDialog(true);
  };

  const toggleSelectForCompare = (id) => {
    setSelectedForCompare(prev => {
      if (prev.includes(id)) {
        return prev.filter(x => x !== id);
      }
      if (prev.length >= 2) {
        return [prev[1], id]; // Replace first selection
      }
      return [...prev, id];
    });
  };

  const goToCompare = () => {
    if (selectedForCompare.length !== 2) {
      toast.error('Please select exactly 2 assessments to compare');
      return;
    }
    window.location.href = `/dashboard/compare?type=${compareType}&a1=${selectedForCompare[0]}&a2=${selectedForCompare[1]}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    );
  }

  const { fearAssessments, archetypeAssessments, totalCompleted, totalInProgress } = data || {};

  const renderAssessmentSection = (type, assessments, icon, colorClass) => {
    const completed = assessments.filter(a => a.status === 'completed');
    const inProgress = assessments.filter(a => a.status === 'in_progress');
    const Icon = icon;

    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Icon className={`w-5 h-5 ${colorClass}`} />
              {type === 'fear' ? 'EDGE Diagnostics' : 'CORE Diagnostics'}
            </CardTitle>
            <div className="flex gap-2">
              {completed.length >= 2 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => openCompareDialog(type, assessments)}
                >
                  <ArrowRightLeft className="w-4 h-4 mr-1" /> Compare
                </Button>
              )}
              {completed.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleRetake(type)}
                >
                  <RefreshCw className="w-4 h-4 mr-1" /> Retake
                </Button>
              )}
            </div>
          </div>
          <CardDescription>
            {completed.length} completed, {inProgress.length} in progress
          </CardDescription>
        </CardHeader>
        <CardContent>
          {assessments.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No {type === 'fear' ? 'EDGE' : 'CORE'} diagnostics yet</p>
              <Button onClick={() => window.location.href = `/${type}`}>
                <Play className="w-4 h-4 mr-2" /> Start {type === 'fear' ? 'EDGE' : 'CORE'} Diagnostic
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {assessments.map((assessment, index) => (
                <div 
                  key={assessment.id}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${assessment.status === 'completed' ? 'bg-green-100' : 'bg-amber-100'}`}>
                      {assessment.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          {type === 'fear' ? 'EDGE' : 'CORE'} Diagnostic #{assessments.length - index}
                        </p>
                        {assessment.isRetake && (
                          <Badge variant="outline" className="text-xs">Retake</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span>{formatDate(assessment.completedAt || assessment.createdAt)}</span>
                        <span>•</span>
                        <span>{assessment.questionCount} questions</span>
                        {assessment.status === 'completed' && assessment.results && (
                          <>
                            <span>•</span>
                            <span className="text-foreground">
                              {type === 'fear' 
                                ? assessment.results.primary_fear_cluster 
                                : assessment.results.primary_archetype}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={assessment.status === 'completed' ? 'default' : 'secondary'}>
                      {assessment.status === 'completed' ? 'Completed' : 'In Progress'}
                    </Badge>
                    {assessment.status === 'completed' ? (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => window.location.href = `/results/${assessment.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" /> View Results
                      </Button>
                    ) : (
                      <Button 
                        size="sm"
                        onClick={() => window.location.href = `/assessment/${type}/${assessment.id}`}
                      >
                        Continue <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div data-testid="user-assessments">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">My Assessments</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {totalCompleted} completed • {totalInProgress} in progress
        </p>
      </div>

      <div className="space-y-6">
        {renderAssessmentSection('fear', fearAssessments || [], AlertCircle, 'text-blue-600')}
        {renderAssessmentSection('archetype', archetypeAssessments || [], Brain, 'text-purple-600')}
      </div>

      {/* Retake Confirmation Dialog */}
      <Dialog open={showRetakeDialog} onOpenChange={setShowRetakeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start New {retakeType === 'fear' ? 'EDGE' : 'CORE'} Diagnostic?</DialogTitle>
            <DialogDescription>
              This will create a new diagnostic while keeping your previous results in history. 
              You can compare your results over time to track your progress.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRetakeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmRetake} disabled={starting}>
              {starting ? 'Starting...' : 'Start New Diagnostic'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare Selection Dialog */}
      <Dialog open={showCompareDialog} onOpenChange={setShowCompareDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Compare Diagnostics</DialogTitle>
            <DialogDescription>
              Select 2 completed diagnostics to compare side-by-side.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2 max-h-[300px] overflow-y-auto">
            {compareAssessments.map((assessment, index) => (
              <div 
                key={assessment.id}
                onClick={() => toggleSelectForCompare(assessment.id)}
                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer border transition-colors ${
                  selectedForCompare.includes(assessment.id) 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:bg-muted/50'
                }`}
              >
                <div>
                  <p className="font-medium">Diagnostic #{compareAssessments.length - index}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(assessment.completedAt)} • 
                    {compareType === 'fear' 
                      ? assessment.results?.primary_fear_cluster 
                      : assessment.results?.primary_archetype}
                  </p>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectedForCompare.includes(assessment.id) 
                    ? 'border-primary bg-primary' 
                    : 'border-muted-foreground'
                }`}>
                  {selectedForCompare.includes(assessment.id) && (
                    <CheckCircle className="w-3 h-3 text-white" />
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompareDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={goToCompare} 
              disabled={selectedForCompare.length !== 2}
            >
              Compare Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
